import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import {
  RefreshCw, Activity, Users, Globe2, Radio, ShieldAlert,
  Layers, TrendingUp, AlertTriangle, MapPin,
} from 'lucide-react';

const RANGES = [
  { id: '1h',  label: '1 h'  },
  { id: '6h',  label: '6 h'  },
  { id: '24h', label: '24 h' },
  { id: '7d',  label: '7 d'  },
];

const TABS = [
  { id: 'overview',   label: 'Visão Geral',      icon: Activity },
  { id: 'clients',    label: 'Top Clientes',     icon: Users },
  { id: 'geo',        label: 'Distribuição',     icon: MapPin },
  { id: 'suspects',   label: 'NXDOMAIN Suspeito', icon: ShieldAlert },
  { id: 'domains',    label: 'Sites Acessados',  icon: Globe2 },
  { id: 'categories', label: 'Categorias',       icon: Layers },
  { id: 'trending',   label: 'Em Crescimento',   icon: TrendingUp },
  { id: 'anomalies',  label: 'Anomalias',        icon: AlertTriangle },
  { id: 'live',       label: 'Logs ao Vivo',     icon: Radio },
];

const NET_LABEL = {
  'bloco-proprio':'Bloco próprio', 'cgnat':'CGNAT', 'privado':'Privado/LAN',
  'bloco-ipv6':'Bloco IPv6', 'externo':'Externo', 'externo-ipv6':'Externo IPv6',
  'loopback':'Local', 'link-local':'Link-local', 'desconhecido':'Desconhecido',
};
const NET_COLOR = {
  'bloco-proprio':'#3b82f6','cgnat':'#06b6d4','privado':'#8b5cf6','bloco-ipv6':'#6366f1',
  'externo':'#f59e0b','externo-ipv6':'#f97316','loopback':'#64748b','link-local':'#64748b','desconhecido':'#94a3b8',
};
const CAT_COLOR = {
  streaming:'#ef4444', social:'#ec4899', mensageria:'#22c55e', 'ads-track':'#f59e0b',
  google:'#4285f4', microsoft:'#00a4ef', apple:'#a3a3a3', cdn:'#14b8a6', gaming:'#8b5cf6',
  ecommerce:'#f97316', financeiro:'#16a34a', adulto:'#be123c', 'malware-c2':'#dc2626',
  'ntp-sys':'#64748b', outros:'#94a3b8',
};
const fmt = n => (n||0).toLocaleString('pt-BR');

function Badge({ text, color }) {
  return <span className="badge" style={{ background: color+'22', color, border:'1px solid '+color+'55' }}>{text}</span>;
}

function BarList({ items, max, color }) {
  const m = max || Math.max(1, ...items.map(i=>i.value));
  return (
    <div className="stack" style={{ gap: 8 }}>
      {items.map((it,i)=>(
        <div key={i}>
          <div className="row-between" style={{ fontSize: 13, marginBottom: 3 }}>
            <span className="mono" style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{it.label}</span>
            <span className="dim">{fmt(it.value)}{it.extra||''}</span>
          </div>
          <div style={{ height: 7, background:'var(--bg-2)', borderRadius: 4, overflow:'hidden' }}>
            <div style={{ width: (100*it.value/m)+'%', height:'100%', background: it.color||color||'#3b82f6', borderRadius:4 }}/>
          </div>
        </div>
      ))}
      {!items.length && <p className="dim" style={{ fontSize:13 }}>Sem dados nesta janela.</p>}
    </div>
  );
}

function AreaChart({ buckets }) {
  if (!buckets || buckets.length<2) return <p className="dim" style={{fontSize:13}}>Coletando dados… (aparece em alguns minutos)</p>;
  const W=920, H=160, pad=4;
  const max = Math.max(1, ...buckets.map(b=>b.total));
  const x = i => pad + i*(W-2*pad)/(buckets.length-1);
  const y = v => H-pad - v*(H-2*pad)/max;
  const line = key => buckets.map((b,i)=>`${i?'L':'M'}${x(i).toFixed(1)},${y(b[key]).toFixed(1)}`).join(' ');
  const area = `${line('total')} L${x(buckets.length-1).toFixed(1)},${H-pad} L${x(0).toFixed(1)},${H-pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:170 }}>
      <path d={area} fill="#3b82f622"/>
      <path d={line('total')} fill="none" stroke="#3b82f6" strokeWidth="2"/>
      <path d={line('recursion')} fill="none" stroke="#22c55e" strokeWidth="1.3" opacity="0.8"/>
      <path d={line('refused')} fill="none" stroke="#ef4444" strokeWidth="1.3" opacity="0.8"/>
    </svg>
  );
}

export default function Monitoring() {
  const [tab, setTab]     = useState('overview');
  const [range, setRange] = useState('1h');
  return (
    <div className="stack-lg">
      <header className="row-between">
        <div>
          <h1 className="h1">Monitor DNS</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Análise das queries dos resolvedores (NS1 master + NS2 slave). Retenção 7 dias.
          </p>
        </div>
        <div className="hrow" style={{ gap: 4, background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:2 }}>
          {RANGES.map(r=>(
            <button key={r.id} onClick={()=>setRange(r.id)}
              className={'btn btn-sm '+(range===r.id?'btn-primary':'btn-ghost')} style={{ fontSize:12 }}>{r.label}</button>
          ))}
        </div>
      </header>

      <div className="tab-bar" style={{ flexWrap:'wrap' }}>
        {TABS.map(t=>{ const Ic=t.icon; return (
          <div key={t.id} className={'tab '+(tab===t.id?'active':'')} onClick={()=>setTab(t.id)}>
            <Ic size={14} style={{ marginRight:6 }}/> {t.label}
          </div>);})}
      </div>

      {tab==='overview'   && <Overview range={range}/>}
      {tab==='clients'    && <TopClients range={range}/>}
      {tab==='geo'        && <Geo range={range}/>}
      {tab==='suspects'   && <Suspects range={range}/>}
      {tab==='domains'    && <TopDomains range={range}/>}
      {tab==='categories' && <Categories range={range}/>}
      {tab==='trending'   && <Trending range={range}/>}
      {tab==='anomalies'  && <Anomalies range={range}/>}
      {tab==='live'       && <LiveLog/>}
    </div>
  );
}

function useFetch(paths, deps) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(()=>{
    setLoading(true);
    Promise.all(Object.entries(paths).map(([k,p])=>api.get(p).then(r=>[k,r.data]).catch(()=>[k,null])))
      .then(rs=>{ setData(Object.fromEntries(rs)); setLoading(false); });
  // eslint-disable-next-line
  }, deps);
  useEffect(()=>{ load(); }, [load]);
  return { data, loading, reload: load };
}

function KPI({ label, value, sub, color }) {
  return (
    <div className="card" style={{ flex:1, minWidth:150 }}>
      <div className="dim" style={{ fontSize:12 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color: color||'inherit' }}>{value}</div>
      {sub && <div className="dim" style={{ fontSize:12 }}>{sub}</div>}
    </div>
  );
}

function Overview({ range }) {
  const { data, loading } = useFetch({
    ov:`/dnsmon/overview?range=${range}`, ts:`/dnsmon/timeseries?range=${range}`,
    qt:`/dnsmon/qtypes?range=${range}`, geo:`/dnsmon/geo?range=${range}`,
  }, [range]);
  const ov=data.ov||{};
  return (
    <div className="stack-lg">
      <div className="hrow" style={{ gap:12, flexWrap:'wrap' }}>
        <KPI label="Total de queries" value={fmt(ov.total)} sub={`${ov.qps||0} q/s`}/>
        <KPI label="Clientes únicos" value={fmt(ov.uniq_clients)}/>
        <KPI label="Domínios únicos" value={fmt(ov.uniq_domains)}/>
        <KPI label="Recursão" value={(ov.recursion_pct||0)+'%'} sub={fmt(ov.recursion)} color="#22c55e"/>
        <KPI label="Recusadas (REFUSED)" value={(ov.refused_pct||0)+'%'} sub={fmt(ov.refused)} color="#ef4444"/>
      </div>
      <div className="card">
        <div className="row-between"><b>Queries ao longo do tempo</b>
          <span className="dim" style={{fontSize:12}}>
            <span style={{color:'#3b82f6'}}>● total</span> <span style={{color:'#22c55e'}}>● recursão</span> <span style={{color:'#ef4444'}}>● recusadas</span>
          </span></div>
        <AreaChart buckets={data.ts?.buckets}/>
      </div>
      <div className="hrow" style={{ gap:12, alignItems:'flex-start', flexWrap:'wrap' }}>
        <div className="card" style={{ flex:1, minWidth:300 }}>
          <b>Tipos de query</b>
          <div style={{ marginTop:10 }}>
            <BarList color="#6366f1" items={(data.qt?.qtypes||[]).map(q=>({label:q.qtype, value:q.queries}))}/>
          </div>
        </div>
        <div className="card" style={{ flex:1, minWidth:300 }}>
          <b>Distribuição por rede</b>
          <div style={{ marginTop:10 }}>
            <BarList items={(data.geo?.distribution||[]).map(d=>({label:NET_LABEL[d.net_class]||d.net_class, value:d.queries, color:NET_COLOR[d.net_class]}))}/>
          </div>
        </div>
      </div>
      {loading && <p className="dim" style={{fontSize:12}}>Atualizando…</p>}
    </div>
  );
}

function TopClients({ range }) {
  const { data, loading } = useFetch({ c:`/dnsmon/top-clients?range=${range}&limit=80` }, [range]);
  const clients=data.c?.clients||[];
  return (
    <div className="card">
      <b>Top clientes — {clients.length} mostrados</b>
      <table className="table" style={{ marginTop:10, width:'100%' }}>
        <thead><tr><th>#</th><th>IP</th><th>Rede</th><th style={{textAlign:'right'}}>Queries</th><th style={{textAlign:'right'}}>Domínios únicos</th></tr></thead>
        <tbody>
          {clients.map((c,i)=>(
            <tr key={c.ip}>
              <td className="dim">{i+1}</td>
              <td className="mono">{c.ip}</td>
              <td><Badge text={NET_LABEL[c.net_class]||c.net_class} color={NET_COLOR[c.net_class]||'#94a3b8'}/></td>
              <td style={{textAlign:'right',fontWeight:600}}>{fmt(c.queries)}</td>
              <td style={{textAlign:'right'}} className="dim">{fmt(c.uniq_domains)}</td>
            </tr>))}
        </tbody>
      </table>
      {loading && <p className="dim" style={{fontSize:12}}>Carregando…</p>}
      {!loading && !clients.length && <p className="dim" style={{fontSize:13}}>Sem dados nesta janela.</p>}
    </div>
  );
}

function Geo({ range }) {
  const { data } = useFetch({ geo:`/dnsmon/geo?range=${range}` }, [range]);
  const dist=data.geo?.distribution||[];
  const totalQ=dist.reduce((a,d)=>a+d.queries,0)||1;
  return (
    <div className="hrow" style={{ gap:12, alignItems:'flex-start', flexWrap:'wrap' }}>
      <div className="card" style={{ flex:1, minWidth:340 }}>
        <b>Distribuição por classe de rede</b>
        <p className="dim" style={{fontSize:12, marginTop:2}}>Resolvedor de ISP: a geografia relevante é a topologia de rede dos assinantes.</p>
        <div style={{ marginTop:12 }}>
          <BarList items={dist.map(d=>({label:NET_LABEL[d.net_class]||d.net_class, value:d.queries, extra:` (${(100*d.queries/totalQ).toFixed(0)}%)`, color:NET_COLOR[d.net_class]}))}/>
        </div>
      </div>
      <div className="card" style={{ flex:1, minWidth:280 }}>
        <b>Clientes por rede</b>
        <table className="table" style={{ marginTop:10, width:'100%' }}>
          <thead><tr><th>Rede</th><th style={{textAlign:'right'}}>Clientes</th><th style={{textAlign:'right'}}>Queries</th></tr></thead>
          <tbody>{dist.map(d=>(
            <tr key={d.net_class}>
              <td><Badge text={NET_LABEL[d.net_class]||d.net_class} color={NET_COLOR[d.net_class]||'#94a3b8'}/></td>
              <td style={{textAlign:'right'}}>{fmt(d.clients)}</td>
              <td style={{textAlign:'right',fontWeight:600}}>{fmt(d.queries)}</td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Suspects({ range }) {
  const { data, loading } = useFetch({ s:`/dnsmon/suspects?range=${range}&limit=80` }, [range]);
  const sus=data.s?.suspects||[];
  return (
    <div className="card">
      <b>Clientes com padrão suspeito (DGA / alta entropia / excesso de domínios)</b>
      <p className="dim" style={{fontSize:12, marginTop:2}}>
        Heurística sobre os nomes consultados — indica possível malware/DGA gerando domínios aleatórios (típico de respostas NXDOMAIN em massa).
      </p>
      <table className="table" style={{ marginTop:10, width:'100%' }}>
        <thead><tr><th>IP</th><th>Rede</th><th style={{textAlign:'right'}}>Score</th><th style={{textAlign:'right'}}>Domínios únicos</th><th>Exemplo</th></tr></thead>
        <tbody>{sus.map(s=>(
          <tr key={s.ip}>
            <td className="mono">{s.ip}</td>
            <td><Badge text={NET_LABEL[s.net_class]||s.net_class} color={NET_COLOR[s.net_class]||'#94a3b8'}/></td>
            <td style={{textAlign:'right'}}><Badge text={(s.score*100).toFixed(0)+'%'} color={s.score>0.6?'#dc2626':s.score>0.4?'#f59e0b':'#64748b'}/></td>
            <td style={{textAlign:'right',fontWeight:600}}>{fmt(s.uniq_domains)}</td>
            <td className="mono dim" style={{fontSize:12}}>{s.sample||'—'}</td>
          </tr>))}</tbody>
      </table>
      {loading && <p className="dim" style={{fontSize:12}}>Carregando…</p>}
      {!loading && !sus.length && <p className="dim" style={{fontSize:13}}>✓ Nenhum cliente suspeito detectado nesta janela.</p>}
    </div>
  );
}

function TopDomains({ range }) {
  const { data, loading } = useFetch({ d:`/dnsmon/top-domains?range=${range}&limit=200` }, [range]);
  const [q,setQ]=useState('');
  const domains=(data.d?.domains||[]).filter(d=>!q||d.domain.includes(q.toLowerCase()));
  return (
    <div className="card">
      <div className="row-between">
        <b>Domínios mais resolvidos</b>
        <input className="input" placeholder="filtrar…" value={q} onChange={e=>setQ(e.target.value)} style={{ maxWidth:200, fontSize:13 }}/>
      </div>
      <table className="table" style={{ marginTop:10, width:'100%' }}>
        <thead><tr><th>#</th><th>Domínio</th><th>Categoria</th><th style={{textAlign:'right'}}>Queries</th></tr></thead>
        <tbody>{domains.map((d,i)=>(
          <tr key={d.domain}>
            <td className="dim">{i+1}</td>
            <td className="mono" style={{fontSize:13}}>{d.domain}</td>
            <td><Badge text={d.category} color={CAT_COLOR[d.category]||'#94a3b8'}/></td>
            <td style={{textAlign:'right',fontWeight:600}}>{fmt(d.queries)}</td>
          </tr>))}</tbody>
      </table>
      {loading && <p className="dim" style={{fontSize:12}}>Carregando…</p>}
    </div>
  );
}

function Categories({ range }) {
  const { data } = useFetch({ c:`/dnsmon/categories?range=${range}` }, [range]);
  const cats=data.c?.categories||[];
  return (
    <div className="card">
      <b>Acessos por categoria</b>
      <div style={{ marginTop:12 }}>
        <BarList items={cats.map(c=>({label:c.category, value:c.queries, extra:` · ${fmt(c.domains)} domínios`, color:CAT_COLOR[c.category]}))}/>
      </div>
    </div>
  );
}

function Trending({ range }) {
  const { data, loading } = useFetch({ t:`/dnsmon/trending?range=${range}` }, [range]);
  const tr=data.t?.trending||[];
  return (
    <div className="card">
      <b>Sites em crescimento</b>
      <p className="dim" style={{fontSize:12, marginTop:2}}>Comparação da janela atual vs. a janela anterior de mesmo tamanho.</p>
      <table className="table" style={{ marginTop:10, width:'100%' }}>
        <thead><tr><th>Domínio</th><th>Categoria</th><th style={{textAlign:'right'}}>Anterior</th><th style={{textAlign:'right'}}>Atual</th><th style={{textAlign:'right'}}>Crescimento</th></tr></thead>
        <tbody>{tr.map(d=>(
          <tr key={d.domain}>
            <td className="mono" style={{fontSize:13}}>{d.domain}</td>
            <td><Badge text={d.category} color={CAT_COLOR[d.category]||'#94a3b8'}/></td>
            <td style={{textAlign:'right'}} className="dim">{fmt(d.prev)}</td>
            <td style={{textAlign:'right',fontWeight:600}}>{fmt(d.cur)}</td>
            <td style={{textAlign:'right'}}><Badge text={(d.growth>=999?'novo':'+'+d.growth+'%')} color="#22c55e"/></td>
          </tr>))}</tbody>
      </table>
      {loading && <p className="dim" style={{fontSize:12}}>Carregando…</p>}
      {!loading && !tr.length && <p className="dim" style={{fontSize:13}}>Sem tendência relevante (precisa de 2 janelas de histórico).</p>}
    </div>
  );
}

function Anomalies({ range }) {
  const { data, loading } = useFetch({ a:`/dnsmon/anomalies?range=${range}` }, [range]);
  const an=data.a?.anomalies||[]; const base=data.a?.baseline||{};
  const T={ 'pico-trafego':{l:'Pico de tráfego',c:'#f59e0b'}, 'recusas-altas':{l:'Recusas altas',c:'#ef4444'} };
  return (
    <div className="card">
      <div className="row-between"><b>Anomalias detectadas</b>
        <span className="dim" style={{fontSize:12}}>baseline ~{fmt(base.mean)} q/bucket (σ {fmt(base.sd)})</span></div>
      <table className="table" style={{ marginTop:10, width:'100%' }}>
        <thead><tr><th>Quando</th><th>Tipo</th><th style={{textAlign:'right'}}>Total no bucket</th><th style={{textAlign:'right'}}>Detalhe</th></tr></thead>
        <tbody>{an.map((a,i)=>(
          <tr key={i}>
            <td className="dim">{new Date(a.t).toLocaleString('pt-BR')}</td>
            <td><Badge text={(T[a.type]||{}).l||a.type} color={(T[a.type]||{}).c||'#94a3b8'}/></td>
            <td style={{textAlign:'right',fontWeight:600}}>{fmt(a.total)}</td>
            <td style={{textAlign:'right'}} className="dim">{a.z?`${a.z}σ`:''}{a.refused_pct!=null?` ${a.refused_pct}% recusadas`:''}</td>
          </tr>))}</tbody>
      </table>
      {loading && <p className="dim" style={{fontSize:12}}>Carregando…</p>}
      {!loading && !an.length && <p className="dim" style={{fontSize:13}}>✓ Nenhuma anomalia nesta janela.</p>}
    </div>
  );
}

function LiveLog() {
  const [rows,setRows]=useState([]);
  const [paused,setPaused]=useState(false);
  const timer=useRef();
  useEffect(()=>{
    const load=()=>api.get('/dnsmon/live?limit=120').then(r=>setRows(r.data.queries||[])).catch(()=>{});
    load();
    timer.current=setInterval(()=>{ if(!paused) load(); }, 3000);
    return ()=>clearInterval(timer.current);
  }, [paused]);
  return (
    <div className="card">
      <div className="row-between">
        <b>Logs ao vivo <span className="dim" style={{fontSize:12}}>(atualiza a cada 3s)</span></b>
        <button className={'btn btn-sm '+(paused?'btn-primary':'btn-ghost')} onClick={()=>setPaused(p=>!p)}>
          <RefreshCw size={12} style={{marginRight:5}}/>{paused?'Retomar':'Pausar'}
        </button>
      </div>
      <div style={{ marginTop:10, maxHeight:520, overflow:'auto', fontSize:12.5 }}>
        <table className="table" style={{ width:'100%' }}>
          <thead><tr><th>Hora</th><th>Cliente</th><th>Domínio</th><th>Tipo</th><th>Categoria</th><th>NS</th></tr></thead>
          <tbody>{rows.map((q,i)=>(
            <tr key={i}>
              <td className="dim">{new Date(q.ts).toLocaleTimeString('pt-BR')}</td>
              <td className="mono">{q.ip}</td>
              <td className="mono">{q.domain}</td>
              <td><span className="badge accent">{q.qtype}</span></td>
              <td><Badge text={q.category} color={CAT_COLOR[q.category]||'#94a3b8'}/></td>
              <td className="dim">{q.server}</td>
            </tr>))}</tbody>
        </table>
        {!rows.length && <p className="dim" style={{fontSize:13}}>Aguardando queries…</p>}
      </div>
    </div>
  );
}
